/**
 * COL-103 — which rent/deposit payment (if any) just flipped to paid and so
 * deserves the celebration pop-up on the tenant dashboard.
 *
 * The rule the ticket asks for is "when payment is made", but a payment being
 * *made* is not a single moment in this product: the tenant uploads proof, an
 * admin attests or reconciliation matches, and only then does
 * `isPaymentEffectivelyPaid` (src/lib/rent.ts, the canonical predicate) turn
 * true. This module therefore works on the RESOLVED paid flag and fires on the
 * transition into it, so the tenant is never congratulated for a payment the
 * rest of the screen still shows as pending.
 *
 * Pure on purpose: the hook owns storage and React, this owns the decision.
 */

export type CelebratableKind = "rent" | "deposit";

/** The shape the dashboard reduces a `payments` row to before asking. */
export type CelebratablePayment = {
  id: string | number | null | undefined;
  type: string | null | undefined;
  amountCents: number | null | undefined;
  dueDate: string | null | undefined;
  /**
   * When the row actually became paid (`paid_at`, else the admin attestation,
   * else the proof review). Null when nothing records it.
   */
  paidAt?: string | null;
  /** Result of the canonical `isPaymentEffectivelyPaid` predicate. */
  paid: boolean;
};

export type PaidCelebration = {
  paymentId: string;
  kind: CelebratableKind;
  amountCents: number | null;
  dueDate: string | null;
  /** Other rows that flipped in the same batch (admin marking 3 months paid). */
  additionalCount: number;
};

/**
 * The month a rent row belongs to, for the pop-up subtitle.
 *
 * `payments.due_date` is a Postgres DATE, so it arrives as "2026-10-01".
 * `new Date("2026-10-01")` is UTC midnight, which is 30 September in every
 * negative-offset time zone — a tenant in New York would be congratulated for
 * September rent they paid in October. Date-only values are therefore built
 * from their parts at local noon; anything with a time component is left to
 * the normal parser.
 */
export function paidPeriodMonthLabel(
  dueDate: string | null | undefined,
  language: string
): string | null {
  if (!dueDate) return null;
  // Match the WHOLE value: slicing first would make every timestamp look
  // date-only and silently take the local-noon branch.
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dueDate).trim());
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12)
    : new Date(dueDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    month: "long",
    year: "numeric",
  });
}

export type ResolvePaidCelebrationResult = {
  /** Null when nothing flipped, or on the seeding pass. */
  celebrate: PaidCelebration | null;
  /** The ids to persist as "already celebrated". Always the paid set. */
  nextSeen: string[];
};

const CELEBRATABLE_TYPES: CelebratableKind[] = ["rent", "deposit"];

/**
 * A payment that settled days ago is history, not news — whatever the stored
 * "already celebrated" set thinks. This is the backstop for the ways that set
 * can go stale: cleared site data, a new device, or a proofs fetch that came
 * back empty and made long-settled manual rows look unpaid for one pass. Rows
 * that record no timestamp at all still qualify, so a real flip is never
 * swallowed by missing data.
 */
export const CELEBRATION_MAX_AGE_MS = 48 * 60 * 60 * 1000;

/**
 * The LATEST of the timestamps that can mark a row paid. Not the first
 * non-null: a row carrying an old `paid_at` that only became *effectively*
 * paid when an admin attested it today would otherwise look like history and
 * lose its celebration.
 */
export function latestPaidTimestamp(
  ...candidates: (string | null | undefined)[]
): string | null {
  let best: { iso: string; time: number } | null = null;
  for (const candidate of candidates) {
    if (!candidate) continue;
    const time = new Date(candidate).getTime();
    if (Number.isNaN(time)) continue;
    if (!best || time > best.time) best = { iso: candidate, time };
  }
  return best?.iso ?? null;
}

function isRecentlyPaid(payment: CelebratablePayment, now: number): boolean {
  if (!payment.paidAt) return true;
  const paidAt = new Date(payment.paidAt).getTime();
  if (Number.isNaN(paidAt)) return true;
  return now - paidAt <= CELEBRATION_MAX_AGE_MS;
}

function kindOf(type: string | null | undefined): CelebratableKind | null {
  const normalized = String(type ?? "").toLowerCase();
  return (CELEBRATABLE_TYPES as string[]).includes(normalized)
    ? (normalized as CelebratableKind)
    : null;
}

/** localStorage key — per user, so a shared browser does not leak celebrations. */
export function paidCelebrationStorageKey(userId: string): string {
  return `coliviq.paidCelebration.v1.${userId}`;
}

/**
 * Ids of the rent/deposit rows that read as paid right now, in a stable order.
 * Exported for the hook's seeding pass and for tests.
 */
export function paidCelebratableIds(
  payments: readonly CelebratablePayment[] | null | undefined
): string[] {
  if (!payments) return [];
  return payments
    .filter((p) => p && p.paid && kindOf(p.type) !== null && p.id != null)
    .map((p) => String(p.id));
}

/**
 * Pick the row to celebrate. A deposit is once in a tenancy and the bigger
 * moment, so it wins over rent; between rent rows the latest due month wins,
 * which is the one the tenant just acted on.
 */
function pickPrimary(candidates: CelebratablePayment[]): CelebratablePayment {
  const deposits = candidates.filter((p) => kindOf(p.type) === "deposit");
  const pool = deposits.length > 0 ? deposits : candidates;
  return [...pool].sort((a, b) => {
    const at = a.dueDate ? new Date(a.dueDate).getTime() : 0;
    const bt = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    if (bt !== at) return bt - at;
    return String(b.id).localeCompare(String(a.id));
  })[0];
}

export function resolvePaidCelebration(input: {
  payments: readonly CelebratablePayment[] | null | undefined;
  /** Ids already celebrated (or seeded). */
  seen: readonly string[] | null | undefined;
  /** Injectable clock for the recency backstop. */
  now?: number;
  /**
   * False on the very first evaluation for this user, when there is nothing
   * stored yet: a tenant who has been paying rent for eight months must not be
   * met with eight months of confetti the first time this ships.
   */
  seeded: boolean;
}): ResolvePaidCelebrationResult {
  const { payments, seen, seeded, now = Date.now() } = input;
  const paidIds = paidCelebratableIds(payments);

  // `nextSeen` is the paid set itself rather than a growing union: it prunes
  // rows that vanished, and an admin who un-marks a payment and marks it again
  // gets a fresh celebration, which is the honest reading of "flipped to paid".
  if (!seeded) return { celebrate: null, nextSeen: paidIds };

  const seenSet = new Set(seen ?? []);
  const flipped = (payments ?? []).filter(
    (p) =>
      p &&
      p.paid &&
      p.id != null &&
      kindOf(p.type) !== null &&
      !seenSet.has(String(p.id)) &&
      isRecentlyPaid(p, now)
  );
  if (flipped.length === 0) return { celebrate: null, nextSeen: paidIds };

  const primary = pickPrimary(flipped);
  return {
    celebrate: {
      paymentId: String(primary.id),
      kind: kindOf(primary.type) as CelebratableKind,
      amountCents:
        primary.amountCents == null ? null : Number(primary.amountCents),
      dueDate: primary.dueDate ?? null,
      additionalCount: flipped.length - 1,
    },
    nextSeen: paidIds,
  };
}
